import { useState } from 'react';
import { SelfieCamera } from '../identity/SelfieCamera';
import { uploadToSignedUrl } from '../../core/api/client';
import { reportError } from '../../core/telemetry/report';

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

/**
 * A PHONE ON HOLD SHOWS ITS FACE (owner, 4 Nov, migration 141).
 *
 * This phone is not the approved one, so it got no session. It got a pass for
 * one live face scan instead; the office sees that face beside the driver's
 * photo before anyone can approve the phone. Live camera only, never a file.
 */
async function post<T>(path: string, hold: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${hold}` },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(json.error ?? `http_${res.status}`);
  return json;
}

export function PhoneHoldScan({ hold, onBack }: { hold: string; onBack: () => void }) {
  const [phase, setPhase] = useState<'intro' | 'camera' | 'saving' | 'done' | 'failed'>('intro');

  const send = async (blob: Blob) => {
    setPhase('saving');
    try {
      const signed = await post<{ uploadUrl: string; objectPath: string; requiredHeaders?: Record<string, string> }>(
        '/phone-hold/sign', hold, { contentType: blob.type || 'image/jpeg' });
      await uploadToSignedUrl(signed.uploadUrl, blob, signed.requiredHeaders);
      await post('/phone-hold/face', hold, { objectPath: signed.objectPath });
      setPhase('done');
    } catch (err) {
      reportError('upload_failed', 'phone_hold', (err as Error).message);
      setPhase('failed');
    }
  };

  if (phase === 'camera') {
    return (
      <main className="screen">
        <SelfieCamera
          hint="Your face, in the frame · Su cara, dentro del marco"
          shutterLabel="Take face scan · Tomar escaneo"
          onCapture={(b) => void send(b)}
          onUnavailable={(m) => { reportError('camera_failed', 'phone_hold', m); setPhase('failed'); }}
        />
      </main>
    );
  }

  return (
    <main className="screen auth-screen">
      {phase === 'intro' && (
        <>
          <h1>This phone is on hold</h1>
          <p>It is not the phone approved for your account. Take a face scan now: the office
             sees it before deciding. Then call the office.</p>
          <p lang="es">Este teléfono no es el aprobado para su cuenta. Tome un escaneo de su cara
             ahora: la oficina lo ve antes de decidir. Después llame a la oficina.</p>
          <button className="btn primary big" onClick={() => setPhase('camera')}>
            Take face scan · Tomar escaneo
          </button>
        </>
      )}
      {phase === 'saving' && <h1>Sending… · Enviando…</h1>}
      {phase === 'done' && (
        <>
          <h1>Sent to the office</h1>
          <p>Call the office. If they approve this phone, the old one stops working.</p>
          <p lang="es">Enviado. Llame a la oficina. Si aprueban este teléfono, el anterior deja de funcionar.</p>
        </>
      )}
      {phase === 'failed' && (
        <>
          <h1>The scan was not sent</h1>
          <p>Sign in again to retry, or call the office.</p>
          <p lang="es">El escaneo no se envió. Inicie sesión de nuevo para intentarlo, o llame a la oficina.</p>
        </>
      )}
      <button className="btn link" onClick={onBack}>Back to sign-in · Volver</button>
    </main>
  );
}
